import React from "react";
import { Link } from "react-router-dom";
import {
  FaDatabase,
  FaUserShield,
  FaShareAlt,
  FaCamera,
  FaMapMarkedAlt,
  FaLock,
  FaCookieBite,
  FaUserEdit,
  FaChild,
  FaSyncAlt,
} from "react-icons/fa";

import MyNavbar from "../../../components/general/navbar/MyNavbar";
import PageHero from "../../../components/general/pageHero/PageHero";
import Footer from "../../../components/general/footer/Footer";
import "../../../assets/css/customer/legal/PrivacyPolicy.css";

const PrivacyPolicy = () => {
  return (
    <div className="terms-page">
      <MyNavbar />
      <PageHero title="Privacy Policy" breadcrumb="privacy" />

      <div className="terms-content-wrapper">
        <p className="terms-updated-date">Last updated: September 2026</p>

        <p className="terms-intro">
          This Privacy Policy explains what personal information
          KisaanMart collects from Customers, Sellers, and Deliverymen,
          how it is used, and the choices available to you. It should be
          read alongside our{" "}
          <Link to="/terms">Terms & Conditions</Link>.
        </p>

        <div className="terms-toc">
          <p className="terms-toc-title">On this page</p>
          <ol>
            <li><a href="#collect">1. Information We Collect</a></li>
            <li><a href="#use">2. How We Use Your Information</a></li>
            <li><a href="#share">3. Who We Share Information With</a></li>
            <li><a href="#images">4. Photos & Order Evidence</a></li>
            <li><a href="#location">5. Location & City Information</a></li>
            <li><a href="#security">6. Data Security</a></li>
            <li><a href="#cookies">7. Cookies & Similar Technologies</a></li>
            <li><a href="#rights">8. Your Choices & Rights</a></li>
            <li><a href="#children">9. Children's Privacy</a></li>
            <li><a href="#changes">10. Changes to This Policy</a></li>
          </ol>
        </div>

        {/* 1 */}
        <section id="collect" className="terms-section">
          <h2 className="terms-section-title">
            <span className="terms-section-number">1</span>
            <FaDatabase /> Information We Collect
          </h2>
          <div className="terms-section-body">
            <p>The information we collect depends on your role on KisaanMart:</p>
            <ul>
              <li><strong>All users:</strong> name, email, phone number, password (stored encrypted), and city.</li>
              <li><strong>Customers:</strong> delivery addresses entered per order, order and payment history, reviews you write.</li>
              <li><strong>Sellers:</strong> shop details, CNIC and KYC documents, bank account details for payouts, product listings.</li>
              <li><strong>Deliverymen:</strong> CNIC, driving license, vehicle documents and a profile photo (required for KYC), current city and service-city preferences, wallet/topup history.</li>
              <li><strong>Payments:</strong> for card payments, KisaanMart does not store your full card details — these are handled directly by our payment processor. For bank transfers, we store the receipt image and amount you submit, and (if a refund is needed) the bank details you provide for that refund.</li>
            </ul>
          </div>
        </section>

        {/* 2 */}
        <section id="use" className="terms-section">
          <h2 className="terms-section-title">
            <span className="terms-section-number">2</span>
            <FaUserShield /> How We Use Your Information
          </h2>
          <div className="terms-section-body">
            <ul>
              <li>To create and manage your account, and verify Seller/Deliveryman KYC submissions.</li>
              <li>To process orders — matching customers with sellers and eligible deliverymen, processing payments and refunds, and calculating earnings, commissions, and cancellation fees.</li>
              <li>To communicate with you — order updates, delivery status, Help & Support conversations, and important notices about your account.</li>
              <li>To investigate and resolve disputes between customers, sellers, and deliverymen.</li>
              <li>To maintain the security of the platform and prevent fraud or abuse.</li>
            </ul>
          </div>
        </section>

        {/* 3 */}
        <section id="share" className="terms-section">
          <h2 className="terms-section-title">
            <span className="terms-section-number">3</span>
            <FaShareAlt /> Who We Share Information With
          </h2>
          <div className="terms-section-body">
            <p>
              We share only what each party needs to complete a
              transaction. For example, when you place an order, the
              relevant Seller sees your delivery address and order
              details, and the Deliveryman you select sees your address
              and phone number so they can complete the delivery.
            </p>
            <p>
              We do not sell your personal information to third parties.
              Limited information may be shared with our payment processor
              (for card payments) and, where legally required, with
              relevant authorities.
            </p>
          </div>
        </section>

        {/* 4 */}
        <section id="images" className="terms-section">
          <h2 className="terms-section-title">
            <span className="terms-section-number">4</span>
            <FaCamera /> Photos & Order Evidence
          </h2>
          <div className="terms-section-body">
            <p>
              At certain points in an order's lifecycle — packing,
              pickup, a rejected/returned delivery, and return
              confirmation — Sellers, Deliverymen, or Customers may be
              asked to upload a photo as evidence. These photos are used
              solely for resolving disputes and are visible only to
              KisaanMart's Admin team — they are never shown to other
              Customers, Sellers, or Deliverymen, and are not used for any
              purpose beyond dispute resolution.
            </p>
          </div>
        </section>

        {/* 5 */}
        <section id="location" className="terms-section">
          <h2 className="terms-section-title">
            <span className="terms-section-number">5</span>
            <FaMapMarkedAlt /> Location & City Information
          </h2>
          <div className="terms-section-body">
            <p>
              Your registered city, delivery addresses, and (for
              Deliverymen) self-declared current city and service-city
              list are used to match orders and delivery jobs
              appropriately. KisaanMart does not track real-time GPS
              location in the background — location information is only
              what you explicitly provide.
            </p>
          </div>
        </section>

        {/* 6 */}
        <section id="security" className="terms-section">
          <h2 className="terms-section-title">
            <span className="terms-section-number">6</span>
            <FaLock /> Data Security
          </h2>
          <div className="terms-section-body">
            <p>
              Passwords are stored using industry-standard hashing and are
              never visible, even to KisaanMart staff. We take reasonable
              technical and organizational measures to protect your
              information, but no online platform can guarantee absolute
              security. If you believe your account has been compromised,
              please contact Help & Support immediately.
            </p>
          </div>
        </section>

        {/* 7 */}
        <section id="cookies" className="terms-section">
          <h2 className="terms-section-title">
            <span className="terms-section-number">7</span>
            <FaCookieBite /> Cookies & Similar Technologies
          </h2>
          <div className="terms-section-body">
            <p>
              KisaanMart uses essential local storage/session tokens to
              keep you logged in and remember basic preferences. We do
              not use these technologies to track you across other
              websites.
            </p>
          </div>
        </section>

        {/* 8 */}
        <section id="rights" className="terms-section">
          <h2 className="terms-section-title">
            <span className="terms-section-number">8</span>
            <FaUserEdit /> Your Choices & Rights
          </h2>
          <div className="terms-section-body">
            <ul>
              <li>You can view and update most of your personal information from your account Settings at any time.</li>
              <li>You can control email/in-app notification preferences from Settings.</li>
              <li>To request deletion of your account or data, contact Help & Support — note that some order/financial records may be retained as required for accounting, dispute-resolution, or legal purposes even after account deletion.</li>
            </ul>
          </div>
        </section>

        {/* 9 */}
        <section id="children" className="terms-section">
          <h2 className="terms-section-title">
            <span className="terms-section-number">9</span>
            <FaChild /> Children's Privacy
          </h2>
          <div className="terms-section-body">
            <p>
              KisaanMart is not directed at children, and registration as
              a Customer, Seller, or Deliveryman requires the account
              holder to be of legal age to enter into a binding agreement
              in their jurisdiction.
            </p>
          </div>
        </section>

        {/* 10 */}
        <section id="changes" className="terms-section">
          <h2 className="terms-section-title">
            <span className="terms-section-number">10</span>
            <FaSyncAlt /> Changes to This Policy
          </h2>
          <div className="terms-section-body">
            <p>
              We may update this Privacy Policy from time to time. The
              date at the top of this page reflects the most recent
              update. Continued use of KisaanMart after a change
              constitutes acceptance of the revised policy.
            </p>
          </div>
        </section>

        <div className="terms-contact-box">
          Questions about your privacy? Reach out via our{" "}
          <Link to="/contact">Contact page</Link> or through Help & Support
          in your account.
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
